import { INITIAL_STATE } from './';
import { ActionTypes } from '../actions/action-types';

const application = (state = INITIAL_STATE.application, action = {}) => {
  switch (action.type) {
    case ActionTypes.APPLICATION_BE_REQUEST:
      return { ...state, activeRequests: state.activeRequests + 1 };
    case ActionTypes.APPLICATION_BE_REQUEST_RESET:
      return { ...state, activeRequests: 0 };
    case ActionTypes.APPLICATION_BE_REQUEST_COMPLETED:
      return {
        ...state,
        activeRequests: state.activeRequests > 0 ? state.activeRequests - 1 : 0
      };
    case ActionTypes.APPLICATION_USER_CREDENTIALS_CLEAR: {
      return { ...state, user: {}, application: {} };
    }
    case ActionTypes.APPLICATION_USER_CREDENTIALS_SET:
      return {
        ...state,
        user: {
          ...state.user,
          id: action.payload.userId,
          hash: action.payload.hash,
          credentials: action.payload.credentials
        }
      };
    case ActionTypes.APPLICATION_USER_CREDENTIALS_REFRESH: {
      const newState = { ...state };
      newState.user = { ...newState.user };
      newState.user.credentials = {
        ...newState.user.credentials,
        encrypted: action.payload.encrypted,
        salt: action.payload.salt
      };
      return newState;
    }
    case ActionTypes.APPLICATION_FOLDER_SELECT:
      return {
        ...state,
        selectedFolderId: action.payload.folderId
      };
    case ActionTypes.APPLICATION_FOLDER_CREATE: {
      return {
        ...state,
        createFolderParentId: action.payload
      };
    }
    case ActionTypes.APPLICATION_FOLDER_RENAME: {
      return {
        ...state,
        renameFolderId: action.payload ? action.payload.folderId : null
      };
    }
    case ActionTypes.APPLICATION_FOLDER_RENAME_OK: {
      const newState = { ...state };
      if (state.selectedFolderId === action.payload.oldFolderId) {
        newState.selectedFolderId = action.payload.newFolderId;
      }
      return newState;
    }
    case ActionTypes.APPLICATION_MESSAGE_FILTER_SET: {
      return { ...state, messageFilterKey: action.payload };
    }
    case ActionTypes.APPLICATION_MESSAGE_FILTER_KEYWORD_SET: {
      return { ...state, messageFilterKeyword: action.payload };
    }
    case ActionTypes.APPLICATION_EVENT_FILTER_KEYWORD_SET: {
      return { ...state, eventFilterKeyword: action.payload };
    }
    case ActionTypes.APPLICATION_SHOW_FILTER_EVENT_FIELDS: {
      return { ...state, showFilterEventFields: action.payload };
    }
    case ActionTypes.APPLICATION_SHOW_SETTINGS_POPOVER: {
      return { ...state, showSettingsPopover: action.payload };
    }
    case ActionTypes.APPLICATION_SHOW_FILTER_SEARCH: {
      return { ...state, showFilterSearch: action.payload };
    }
    case ActionTypes.APPLICATION_SHOW_USER_SIGN_POPOVER: {
      return { ...state, showUserSignPopover: action.payload };
    }
    case ActionTypes.APPLICATION_SHOW_USER_TEMPLATE_POPOVER: {
      return { ...state, showUserTemplatePopover: action.payload };
    }
    case ActionTypes.APPLICATION_SHOW_DEFAULT_CALENDAR_POPOVER: {
      return { ...state, showDefaultCalendarPopover: action.payload };
    }
    case ActionTypes.APPLICATION_IS_FILTER_DATE: {
      return { ...state, isFilterDate: action.payload };
    }
    case ActionTypes.APPLICATION_IS_SEARCH_TOP_BAR: {
      return { ...state, isSearchTopBar: action.payload };
    }
    case ActionTypes.APPLICATION_MESSAGE_FILTER_MESSAGES_LOADING: {
      return { ...state, loadingFilterMessages: true };
    }
    case ActionTypes.APPLICATION_MESSAGE_FILTER_MESSAGES_SET: {
      return {
        ...state,
        filteredMessages: action.payload,
        loadingFilterMessages: false
      };
    }
    case ActionTypes.APPLICATION_MESSAGE_SELECT:
      return { ...state, selectedMessage: { ...action.payload } };
    case ActionTypes.APPLICATION_MESSAGE_REFRESH: {
      const newState = { ...state };
      const message = action.payload.message;
      newState.downloadedMessages = { ...newState.downloadedMessages };
      // Store in application.downloadedMessages
      newState.downloadedMessages[message.messageId] = structuredClone(message);
      // Update selected message if is currently selected.
      // Two ways to match:
      //  - same folder as the one currently selected + same uid (classic single-folder flow)
      //  - same messageId (+ same folder name when the selected message knows its folder):
      //    needed for multi-folder search results, where selectedFolderId may not be the
      //    folder the message lives in (PEC-8348). uid alone is only unique per folder.
      const selected = state.selectedMessage || {};
      const payloadFolder = action.payload.folder || {};
      const sameFolderAndUid =
        state.selectedFolderId === payloadFolder.folderId && selected.uid === message.uid;
      const selectedFolderName =
        selected.folderFullName || (selected.folder && selected.folder.fullName);
      const sameMessageIdAndFolder =
        Boolean(message.messageId) &&
        selected.messageId === message.messageId &&
        (!selectedFolderName || selectedFolderName === payloadFolder.fullName);
      if (sameFolderAndUid || sameMessageIdAndFolder) {
        newState.selectedMessage = { ...message };
      }
      return newState;
    }
    case ActionTypes.APPLICATION_MESSAGE_REFRESH_BE_REQUEST:
      return {
        ...state,
        refreshMessageActiveRequests: state.refreshMessageActiveRequests + 1
      };
    case ActionTypes.APPLICATION_MESSAGE_REFRESH_BE_REQUEST_RESET:
      return { ...state, refreshMessageActiveRequests: 0 };
    case ActionTypes.APPLICATION_MESSAGE_REFRESH_BE_REQUEST_COMPLETED:
      return {
        ...state,
        refreshMessageActiveRequests:
          state.refreshMessageActiveRequests > 0
            ? state.refreshMessageActiveRequests - 1
            : 0
      };
    case ActionTypes.APPLICATION_MESSAGE_PRE_DOWNLOAD: {
      const newState = { ...state };
      const messages = action.payload.messages;
      // Store in application.downloadedMessages
      messages.forEach(message => {
        newState.downloadedMessages[message.messageId] = structuredClone(message);
      });
      return newState;
    }
    case ActionTypes.APPLICATION_MESSAGE_REPLACE_IMAGE: {
      const newState = { ...state };
      const folder = action.payload.folder;
      const message = action.payload.message;
      const attachment = action.payload.attachment;
      const contentId = attachment.contentId.replace(/[<>]/g, '');
      const regex = new RegExp(`cid:${contentId}`, 'g'); // Multiple occurrence -> ReplaceAll
      if (contentId.length > 0) {
        const objectUrl = URL.createObjectURL(action.payload.blob);
        // Store in application.downloadedMessages
        newState.downloadedMessages[message.messageId].content =
          newState.downloadedMessages[message.messageId].content.replace(
            regex,
            objectUrl
          );
        // Update selected message if applicable
        if (
          newState.selectedFolderId === folder.folderId &&
          newState.selectedMessage.uid === message.uid
        ) {
          const parsedMessage = newState.selectedMessage.content.replace(
            regex,
            objectUrl
          );
          newState.selectedMessage = {
            ...newState.selectedMessage,
            content: parsedMessage
          };
        }
      }
      return newState;
    }
    case ActionTypes.APPLICATION_MESSAGE_REPLACE_IMAGE_BASE64: {
      const newState = { ...state };
      const folder = action.payload.folder;
      const message = action.payload.message;
      const attachment = action.payload.attachment;
      const contentId = attachment.contentId.replace(/[<>]/g, '');
      const regex = new RegExp(`cid:${contentId}`, 'g'); // Multiple occurrence -> ReplaceAll
      if (contentId.length > 0) {
        // Store in application.downloadedMessages
        newState.downloadedMessages[message.messageId].content =
          newState.downloadedMessages[message.messageId].content.replace(
            regex,
            action.payload.base64
          );
        // Update selected message if applicable
        if (
          newState.selectedFolderId === folder.folderId &&
          newState.selectedMessage.uid === message.uid
        ) {
          const parsedMessage = newState.selectedMessage.content.replace(
            regex,
            action.payload.base64
          );
          newState.selectedMessage = {
            ...newState.selectedMessage,
            content: parsedMessage
          };
        }
        if (newState.newMessage) {
          const newMessage = newState.newMessage.content.replace(
            regex,
            action.payload.base64
          );
          newState.newMessage.content = {
            ...newState.newMessage,
            content: newMessage
          };
        }
      }
      return newState;
    }
    case ActionTypes.APPLICATION_ERROR_SET: {
      const errorsSetState = { ...state };
      errorsSetState.errors = { ...state.errors };
      errorsSetState.errors[action.payload.type] = action.payload.value;
      return errorsSetState;
    }

    // Add this new case to clear authentication errors
    case ActionTypes.APPLICATION_ERROR_CLEAR: {
      const errorsClearState = { ...state };
      errorsClearState.errors = { ...state.errors };
      errorsClearState.errors[action.payload.type] = null;
      return errorsClearState;
    }

    case ActionTypes.APPLICATION_INVALID_AUTH: {
      const newState = { ...state, isInvalidAuth: action.payload };
      return newState;
    }
    case ActionTypes.APPLICATION_MESSAGE_EDIT: {
      const newState = { ...state };
      newState.newMessage = action.payload ? { ...action.payload } : null;
      return newState;
    }
    case ActionTypes.APPLICATION_OUTBOX_SEND: {
      const newState = { ...state };
      newState.outbox = { message: action.payload, progress: 0, sent: false };
      return newState;
    }
    case ActionTypes.APPLICATION_OUTBOX_UPDATE_PROGRESS: {
      const newState = { ...state };
      newState.outbox = { ...newState.outbox, progress: action.payload };
      return newState;
    }
    case ActionTypes.APPLICATION_DRAFT_UPDATE_PROGRESS: {
      const newState = { ...state };
      newState.draft = { ...newState.draft, progress: action.payload };
      return newState;
    }
    case ActionTypes.APPLICATION_OUTBOX_SET_SENT: {
      const newState = { ...state };
      newState.outbox = {
        ...newState.outbox,
        sent: action.payload.sent,
        idMessage: action.payload.idMessage,
        eventNotified: action.payload.eventNotified
      };
      return newState;
    }
    case ActionTypes.APPLICATION_DRAFT_SET_SENT: {
      const newState = { ...state };
      newState.draft = {
        ...newState.draft,
        sent: action.payload.sent,
        idMessage: action.payload.idMessage,
        eventNotified: action.payload.eventNotified
      };
      return newState;
    }
    case ActionTypes.APPLICATION_OUTBOX_SET_ERROR: {
      const newState = { ...state };
      newState.outbox = { ...newState.outbox, error: action.payload };
      return newState;
    }
    case ActionTypes.APPLICATION_DRAFT_SET_ERROR: {
      const newState = { ...state };
      newState.draft = { ...newState.draft, error: action.payload };
      return newState;
    }
    case ActionTypes.APPLICATION_OUTBOX_MESSAGE_PROCESSED: {
      const newState = { ...state };
      newState.outbox = null;
      return newState;
    }
    case ActionTypes.APPLICATION_DRAFT_CLEAN: {
      const newState = { ...state };
      newState.draft = null;
      return newState;
    }
    case ActionTypes.APPLICATION_OUTBOX_EVENT_NOTIFIED: {
      const newState = { ...state };
      newState.outbox = { ...newState.outbox, eventNotified: true };
      return newState;
    }
    case ActionTypes.APPLICATION_NEXTCLOUD_USER: {
      const newState = { ...state };
      newState.nextcloudUser = action.payload;
      return newState;
    }
    case ActionTypes.APPLICATION_SERVICE: {
      const newState = { ...state };
      newState.service = action.payload;
      return newState;
    }
    case ActionTypes.APPLICATION_SHOW_PREVIEW: {
      return {...state, showPreview: action.payload};
    }

    case ActionTypes.MESSAGES_SET_FILTERS_APPLIED:
      return {
        ...state,
        filtersApplied: action.payload.filtersApplied || {},
        filteredMessages: action.payload.filteredMessages || [],
        searchTotalHits: action.payload.totalHits || 0,
        searchFrom: action.payload.from || 0,
        searchSize: action.payload.size || 50,
        selectedMessage: {}
      };

    case ActionTypes.MESSAGES_APPEND_FILTERED_MESSAGES:
      return {
        ...state,
        filteredMessages: [...state.filteredMessages, ...action.payload.newMessages],
        searchFrom: action.payload.from,
        searchSize: action.payload.size
      };

    case ActionTypes.APPLICATION_CLEAR_FILTERED_MESSAGES:
      return {
        ...state,
        filtersApplied: {},
        filteredMessages: [],
        searchTotalHits: 0,
        searchFrom: 0,
        searchSize: 50,
        selectedFolder: null,
        selectedMessage: {}
      };
      
    default:
      return state;
  }
};

export default application;
